import React from "react";
import {Input, Tag, Tree} from "antd";
import {getTypeBadge} from "./typeBadge.js";
import brand from "@/assets/brand.webp";

export default function OrgTree({treeData, onSelect, filter, showExtras, expandedKeys, onExpandedChange, selectedKey, highlightKey})
{
	const filterFn = (q, list) =>
	{
		if (!q) return list;

		const needle = q.toLowerCase();
		const walk = (arr) =>
		{
			const out = [];
			for (const n of arr)
			{
				const title = n.title?.name || "";
				const kids = n.children?.length ? walk(n.children) : [];
				const match = title.toLowerCase().includes(needle);

				if (match || kids.length)
					out.push({...n, children: kids});
			}
			return out;
		};
		return walk(list);
	};

	const mapTitle = (n) => ({
		...n,
		title: (
			<NodeRow
				name={n.title?.name}
				type={n.title?.type}
				meta={n.title?.meta}
				showExtras={showExtras}
			/>
		),
		children: n.children?.length ? n.children.map(mapTitle) : []
	});

	const data = React.useMemo(() =>
	{
		return filterFn(filter, treeData).map(mapTitle);
	}, [filter, treeData, showExtras]);

	const collectDescendantKeys = React.useCallback((node) =>
	{
		const keys = [];
		const stack = Array.isArray(node?.children) ? [...node.children] : [];

		while (stack.length)
		{
			const current = stack.pop();
			if (!current)
				continue;

			if (current.key != null)
				keys.push(current.key);

			if (Array.isArray(current.children) && current.children.length)
				stack.push(...current.children);
		}

		return keys;
	}, []);

	// Se chiudi un nodo rimuovi i discendenti da expandedKeys per permettere il collapse del ramo
	const handleExpand = React.useCallback((keys, info) =>
	{
		if (info?.expanded === false)
		{
			const descendants = new Set(collectDescendantKeys(info.node));
			if (info.node?.key != null)
				descendants.add(info.node.key);

			const nextKeys = keys.filter(key => !descendants.has(key));
			onExpandedChange(nextKeys);
			return;
		}

		onExpandedChange(keys);
	}, [collectDescendantKeys, onExpandedChange]);

	const handleSelect = React.useCallback((keys, info) =>
	{
		if (!keys?.length)
			return;

		const nodeKey = keys[0];
		onSelect(nodeKey);

		const hasChildren = Array.isArray(info?.node?.children) && info.node.children.length > 0;
		if (!hasChildren)
			return;

		if (expandedKeys.includes(nodeKey))
			return;

		onExpandedChange(Array.from(new Set([...expandedKeys, nodeKey])));
	}, [expandedKeys, onExpandedChange, onSelect]);

	return (
		<div>
			<Input.Search allowClear placeholder="Cerca…" style={{marginBottom: 8}}/>
			<Tree
				blockNode
				showLine={{showLeafIcon: false}}
				treeData={data}
				expandedKeys={expandedKeys}
				onExpand={handleExpand}
				autoExpandParent
				selectedKeys={selectedKey ? [selectedKey] : []}
				onSelect={handleSelect}
				style={{background: "transparent"}}
			/>
		</div>
	);
}

function NodeRow({name, type, meta, showExtras})
{
	return (
		<span className="node-row">
			<span className="node-left">
				<Dot type={type}/>
				<span className="node-name">{name}</span>
			</span>

			<RightBadges type={type} meta={meta} showExtras={showExtras}/>
		</span>
	);
}

function RightBadges({type, meta, showExtras})
{
	const badge = getTypeBadge(type);

	return (
		<span className="node-right">
			{showExtras && meta?.pdv != null && <Tag color="default">PdV: {meta.pdv}</Tag>}
			{showExtras && meta?.flyers != null && <Tag color="default">Volantini: {meta.flyers}</Tag>}
			<Tag color={badge.color} style={badge.style}>{badge.label}</Tag>
		</span>
	);
}

function Dot({type})
{
	if (type === "ROOT")
		return <img src={brand} alt="VolantinoPiù" className="org-root-logo"/>

	if (type === "CONSORZIO")
		return <span className="dot cons"/>;

	if (type === "COOPERATIVA")
		return <span className="dot coop"/>;

	return <span className="dot can"/>;
}
